import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import LanguageSelector from './LanguageSelector';
import { interviewsIndex, interviewsPageContent } from '../data/interviews';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_cc896aae-282d-4475-9628-943e35aab3be/artifacts/d085f74a_Business%20Club%20France%20Latvia_SSa-R00a_Mil.png';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isInterviewsOpen, setIsInterviewsOpen] = useState(false);
  const interviewsRef = useRef(null);
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const iv = interviewsPageContent[language] || interviewsPageContent.fr;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (interviewsRef.current && !interviewsRef.current.contains(e.target)) setIsInterviewsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setIsInterviewsOpen(false);
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const teamLabel =
    language === 'fr' ? 'Équipe' : language === 'lv' ? 'Komanda' : 'Team';

  const interviewItems = [
    { to: '/interviews', label: iv.navAll, testId: 'interviews-all', emphasis: true },
    ...interviewsIndex.map((item) => ({
      to: item.slug,
      label: item.person,
      sub: item.role[language] || item.role.fr,
      testId: item.slug.replace(/^\//, ''),
      isNew: item.isNew,
    })),
  ];

  const navLinks = [
    { id: 'mission', label: t.nav.mission, type: 'section' },
    { id: 'services', label: t.nav.services, type: 'section' },
    { id: 'interviews', label: iv.navLabel, type: 'dropdown' },
    { id: 'team', label: teamLabel, type: 'route', to: '/team' },
    { id: 'fonctionnement', label: t.nav.process, type: 'section' },
    { id: 'faq', label: t.nav.faq, type: 'section' },
    { id: 'contact', label: t.nav.contact, type: 'section' },
  ];

  const handleNavClick = (link) => {
    setIsMobileMenuOpen(false);
    if (link.type === 'route') {
      navigate(link.to);
      return;
    }
    goToSection(link.id);
  };

  const goToSection = (id) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      navigate(`/#${id}`);
      // delay scroll until home renders
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 80);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  // On homepage mount with a hash, scroll to it
  useEffect(() => {
    if (location.pathname === '/' && location.hash) {
      const id = location.hash.replace('#', '');
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 80);
    }
  }, [location]);

  const goHome = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#1a1c1b]/95 backdrop-blur-md border-b border-[#3f4816]/50' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[87.5rem] mx-auto px-6 md:px-10 py-3 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={goHome}
          className="flex items-center gap-3 hover:opacity-80 transition-opacity"
          data-testid="header-logo"
        >
          <img
            src={LOGO_URL}
            alt="Business Club France Latvia"
            className="h-12 md:h-14 w-auto object-contain"
          />
        </button>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) =>
            link.type === 'dropdown' ? (
              <div key={link.id} ref={interviewsRef} className="relative">
                <button
                  onClick={() => setIsInterviewsOpen((o) => !o)}
                  aria-expanded={isInterviewsOpen}
                  aria-haspopup="menu"
                  className="nav-link inline-flex items-center gap-1.5 text-[#d9fb06] text-sm font-medium px-4 py-2 hover:opacity-70 transition-all"
                  data-testid="nav-link-interviews"
                >
                  {link.label}
                  <ChevronDown
                    size={14}
                    className={`transition-transform duration-200 ${isInterviewsOpen ? 'rotate-180' : ''}`}
                  />
                </button>
                {isInterviewsOpen && (
                  <div
                    role="menu"
                    data-testid="interviews-dropdown"
                    className="absolute left-0 top-full mt-2 w-80 bg-[#1a1c1b]/98 backdrop-blur-md border border-[#3f4816] rounded-sm shadow-2xl shadow-black/50 py-2 z-50"
                  >
                    {interviewItems.map((item) => (
                      <Link
                        key={item.to}
                        to={item.to}
                        role="menuitem"
                        data-testid={`interviews-menu-${item.testId}`}
                        onClick={() => setIsInterviewsOpen(false)}
                        className={`block px-5 py-3 hover:bg-[#302f2c] transition-colors ${
                          item.emphasis ? 'border-b border-[#3f4816]/70 mb-1' : ''
                        }`}
                      >
                        <span className={`flex items-center gap-2 text-sm ${item.emphasis ? 'text-[#d9fb06] font-bold uppercase tracking-wider text-xs' : 'text-white font-semibold'}`}>
                          {item.label}
                          {item.isNew && (
                            <span className="bg-[#d9fb06] text-[#1a1c1b] text-[0.55rem] font-black tracking-[0.15em] uppercase px-2 py-0.5 rounded-full">
                              {iv.badgeNew}
                            </span>
                          )}
                        </span>
                        {item.sub && <span className="block text-[#888680] text-xs mt-0.5 leading-snug">{item.sub}</span>}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <button
                key={link.id}
                onClick={() => handleNavClick(link)}
                className="nav-link text-[#d9fb06] text-sm font-medium px-4 py-2 hover:opacity-70 transition-all"
                data-testid={`nav-link-${link.id}`}
              >
                {link.label}
              </button>
            )
          )}
        </nav>

        {/* Language Selector & Mobile Menu */}
        <div className="flex items-center gap-4">
          <LanguageSelector />

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-[#d9fb06] p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <nav className="lg:hidden bg-[#1a1c1b] border-t border-[#3f4816]/50 px-6 py-4">
          {navLinks.map((link) =>
            link.type === 'dropdown' ? (
              <div key={link.id} className="py-2" data-testid="mobile-interviews-group">
                <span className="block text-[#d9fb06] text-sm font-medium py-1">{link.label}</span>
                <div className="pl-4 border-l border-[#3f4816]">
                  {interviewItems.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      data-testid={`mobile-interviews-menu-${item.testId}`}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="block py-2.5 hover:opacity-70 transition-all"
                    >
                      <span className={`text-sm ${item.emphasis ? 'text-[#d9fb06] font-semibold' : 'text-white'}`}>
                        {item.label}
                      </span>
                      {item.sub && <span className="block text-[#888680] text-xs mt-0.5">{item.sub}</span>}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <button
                key={link.id}
                onClick={() => handleNavClick(link)}
                className="block w-full text-left text-[#d9fb06] text-sm font-medium py-3 hover:opacity-70 transition-all"
                data-testid={`mobile-nav-link-${link.id}`}
              >
                {link.label}
              </button>
            )
          )}
        </nav>
      )}
    </header>
  );
};

export default Header;
